/**
 * My Garage: customer-saved machines (localStorage) + fitment UI.
 *
 * window.TeracheGarage — storage API. Fires `garage:change` on document.
 * <machine-picker>     — cascading Year/Make/Model selects.
 * <garage-drawer>      — header drawer to add/switch/remove machines.
 * <garage-button>      — header trigger showing the active machine.
 * <garage-fit-badge>   — product card badge, shown when the product fits.
 */
(() => {
  if (window.TeracheGarage) return;

  const STORAGE_KEY = 'terache_garage';
  const MACHINES_CACHE_KEY = 'terache_machines';
  const MAX_MACHINES = 5;

  const keyOf = (m) => `${m.handle}:${m.year}`;

  const read = () => {
    try {
      const data = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (data && Array.isArray(data.machines)) return data;
    } catch (e) {}
    return { machines: [], active: null };
  };

  const write = (data) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {}
    document.dispatchEvent(new CustomEvent('garage:change', { detail: data }));
  };

  let machinesPromise;

  const Garage = {
    keyOf,
    get: read,

    active() {
      const data = read();
      return data.machines.find((m) => keyOf(m) === data.active) || null;
    },

    add({ handle, id, year, make, model }) {
      const entry = { handle, id, year, make, model };
      const data = read();
      data.machines = [entry, ...data.machines.filter((m) => keyOf(m) !== keyOf(entry))].slice(0, MAX_MACHINES);
      data.active = keyOf(entry);
      write(data);
    },

    remove(key) {
      const data = read();
      data.machines = data.machines.filter((m) => keyOf(m) !== key);
      if (data.active === key) data.active = data.machines[0] ? keyOf(data.machines[0]) : null;
      write(data);
    },

    setActive(key) {
      const data = read();
      data.active = key;
      write(data);
    },

    label: (m) => `${m.year} ${m.make} ${m.model}`,

    open() {
      document.dispatchEvent(new Event('garage:open'));
    },

    loadMachines() {
      if (!machinesPromise) {
        let cached = null;
        try {
          cached = JSON.parse(sessionStorage.getItem(MACHINES_CACHE_KEY));
        } catch (e) {}
        machinesPromise = cached
          ? Promise.resolve(cached)
          : fetch(`${window.Shopify?.routes?.root || '/'}?section_id=machine-data`)
              .then((response) => response.text())
              .then((html) => {
                const doc = new DOMParser().parseFromString(html, 'text/html');
                const machines = JSON.parse(doc.querySelector('[data-machines]').textContent);
                try {
                  sessionStorage.setItem(MACHINES_CACHE_KEY, JSON.stringify(machines));
                } catch (e) {}
                return machines;
              });
      }
      return machinesPromise;
    },
  };

  window.TeracheGarage = Garage;

  // Keep other tabs in sync.
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) document.dispatchEvent(new CustomEvent('garage:change', { detail: read() }));
  });

  customElements.define('machine-picker', class extends HTMLElement {
    connectedCallback() {
      if (this.year) return;
      this.year = this.querySelector('[name="year"]');
      this.make = this.querySelector('[name="make"]');
      this.model = this.querySelector('[name="model"]');

      this.year.addEventListener('change', () => { this.fillMakes(); this.fillModels(); this.emit(); });
      this.make.addEventListener('change', () => { this.fillModels(); this.emit(); });
      this.model.addEventListener('change', () => this.emit());

      Garage.loadMachines().then((machines) => {
        this.machines = machines;
        const years = new Set();
        machines.forEach((m) => { for (let y = m.ys; y <= m.ye; y++) years.add(y); });
        this.fill(this.year, 'Year', [...years].sort((a, b) => b - a));
      });
    }

    fill(select, label, values) {
      select.replaceChildren(new Option(label, ''), ...values.map((v) => new Option(v, v)));
      select.disabled = values.length === 0;
    }

    inYear() {
      const y = Number(this.year.value);
      return (this.machines || []).filter((m) => y >= m.ys && y <= m.ye);
    }

    fillMakes() {
      const makes = this.year.value ? [...new Set(this.inYear().map((m) => m.make))].sort() : [];
      this.fill(this.make, 'Make', makes);
    }

    fillModels() {
      const models = this.make.value
        ? [...new Set(this.inYear().filter((m) => m.make === this.make.value).map((m) => m.model))].sort()
        : [];
      this.fill(this.model, 'Model', models);
    }

    get selected() {
      if (!this.model?.value) return null;
      const m = this.inYear().find((m) => m.make === this.make.value && m.model === this.model.value);
      return m ? { ...m, year: this.year.value } : null;
    }

    focusEmpty() {
      [this.year, this.make, this.model].find((el) => !el.disabled && !el.value)?.focus();
    }

    reset() {
      this.year.value = '';
      this.fillMakes();
      this.fillModels();
    }

    emit() {
      this.dispatchEvent(new CustomEvent('machine:change', { bubbles: true, detail: this.selected }));
    }
  });

  customElements.define('garage-drawer', class extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.list = this.querySelector('.garage-drawer__list');
      this.picker = this.querySelector('machine-picker');
      this.addButton = this.querySelector('.garage-drawer__add-button');

      document.addEventListener('garage:open', () => this.open());
      document.addEventListener('garage:change', () => this.render());
      this.querySelector('.garage-drawer__close').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });

      this.addButton.addEventListener('click', () => {
        const machine = this.picker.selected;
        if (!machine) return this.picker.focusEmpty();
        Garage.add(machine);
        this.picker.reset();
      });

      this.list.addEventListener('change', (event) => {
        if (event.target.name === 'garage-active') Garage.setActive(event.target.value);
      });
      this.list.addEventListener('click', (event) => {
        const button = event.target.closest('[data-remove]');
        if (button) Garage.remove(button.dataset.remove);
      });

      this.render();
    }

    // Desktop: small box under the header button. Phones (button hidden): full-height side drawer.
    open() {
      const anchor = document.querySelector('.garage-button--header .garage-button__trigger');
      const rect = anchor?.getBoundingClientRect();
      const dropdown = Boolean(rect?.width);
      this.dialog.toggleAttribute('data-dropdown', dropdown);
      if (dropdown) {
        this.dialog.style.setProperty('--garage-top', `${rect.bottom + 8}px`);
        this.dialog.style.setProperty('--garage-right', `${Math.max(16, window.innerWidth - rect.right)}px`);
      }
      this.dialog.showModal();
    }

    render() {
      const { machines, active } = Garage.get();
      this.querySelector('.garage-drawer__empty').hidden = machines.length > 0;
      this.list.replaceChildren(
        ...machines.map((m) => {
          const key = keyOf(m);
          const li = document.createElement('li');
          li.className = 'garage-drawer__item';
          li.innerHTML = `
            <label class="garage-drawer__machine">
              <input type="radio" name="garage-active">
              <span></span>
            </label>
            <button type="button" class="garage-drawer__remove button-unstyled">Remove</button>`;
          const radio = li.querySelector('input');
          radio.value = key;
          radio.checked = key === active;
          li.querySelector('span').textContent = Garage.label(m);
          li.querySelector('button').dataset.remove = key;
          return li;
        })
      );
    }
  });

  customElements.define('garage-button', class extends HTMLElement {
    connectedCallback() {
      this.querySelector('button').addEventListener('click', () => Garage.open());
      document.addEventListener('garage:change', () => this.render());
      // The menu drawer copy can be reset by header re-renders; refresh when a <details> ancestor opens.
      document.addEventListener('toggle', (event) => {
        if (event.target.contains(this)) this.render();
      }, true);
      this.render();
    }

    render() {
      const machine = Garage.active();
      this.querySelector('.garage-button__label').textContent = machine ? `${machine.make} ${machine.model}` : this.dataset.emptyLabel;
      this.toggleAttribute('data-active', Boolean(machine));
    }
  });

  customElements.define('garage-fit-badge', class extends HTMLElement {
    connectedCallback() {
      this.onChange = () => this.render();
      document.addEventListener('garage:change', this.onChange);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('garage:change', this.onChange);
    }

    render() {
      const machine = Garage.active();
      const fits = Boolean(machine) && this.dataset.handles.split(',').includes(machine.handle);
      this.hidden = !fits;
      if (fits) this.querySelector('.garage-fit-badge__text').textContent = `Fits your ${machine.model}`;
    }
  });
})();
